import React, { useState } from 'react'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Grid, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { TextField } from '@e3dc-react/shell/Elements/InputFields'
import { UserData } from '../../State/User/UserModel'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useValidPassword } from '@e3dc-react/shell/Libs/validation'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { FetchErrorCodes } from '@e3dc-react/shell/Libs'

const useStyles = makeStyles()(theme => ({
  formItem: {
    marginBottom: theme.spacing(2),
  },
}))

export interface PasswordEditorProps {
  place: number
  onClose: () => void
  user: UserData
}

const PasswordEditor: React.FC<PasswordEditorProps> = ({ place, onClose, user }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const authFetch = useAuthFetchWithResult()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const [savingPassword, setSavingPassword] = useState(false)
  const [currentPassword, setCurrentPassword] = useState<string>('')
  const [newPassword, setNewPassword] = useState<string>('')
  const [newPasswordRepeat, setNewPasswordRepeat] = useState<string>('')

  const { valid, errorMessage } = useValidPassword(newPassword, newPasswordRepeat, currentPassword)

  const onSavePassword = async (): Promise<void> => {
    setSavingPassword(true)

    if (!valid) {
      setSavingPassword(false)
      return
    }

    try {
      const payload = { password: newPassword, currentPassword }

      await authFetch({
        method: 'PUT',
        path: `users/${user.ID}`,
        payload,
      })

      setSavingPassword(false)
      onClose()
      enqueueSuccessStack(t('user.save.passwordSuccess'))
    } catch (err) {
      const error = err as AuthFetchError
      console.error(error)

      setSavingPassword(false)

      if (error.response?.data.errno === FetchErrorCodes.PasswordPartOfBreach) {
        enqueueErrorStack(t('user.password-breach'))
        return
      } else if (error.response?.data.errno === FetchErrorCodes.CurrentUserPasswordInvalid) {
        enqueueErrorStack(t('user.confirm-current-password-failed'))
        return
      }
      enqueueErrorStack(t('user.save.passwordError'))
    }
  }

  return (
    <Dialog
      title={t('user.changePassword')}
      onClose={() => onClose()}
      place={place}
      actions={{
        primary: {
          name: t('common.save'),
          onClick: onSavePassword,
          isLoading: savingPassword,
          disabled: !valid,
        },
        secondary: [
          {
            name: t('common.cancel'),
          },
        ],
      }}
    >
      <Grid container>
        <Grid item xs={12} marginTop={4}>
          <TextField
            label={t('user.confirmOldPassword')}
            value={currentPassword}
            onChange={setCurrentPassword}
            type="password"
            disableAutoComplete
            fullWidth
            className={classes.formItem}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={t('user.newPassword')}
            value={newPassword}
            onChange={setNewPassword}
            type="password"
            disableAutoComplete
            fullWidth
            className={classes.formItem}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={t('user.newPasswordRepeat')}
            value={newPasswordRepeat}
            onChange={setNewPasswordRepeat}
            type="password"
            fullWidth
            className={classes.formItem}
          />
        </Grid>
        {newPassword.length > 0 && errorMessage && (
          <Grid item xs={12}>
            <Typography variant="body1" color="error">
              {errorMessage}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Dialog>
  )
}

export default PasswordEditor
